import clsx from "clsx";
import { ComponentProps, useMemo } from "react";
import { useSnapshot } from "valtio";

import { updateShowWorkbench } from "src/js/stores/map/actions/updateShowWorkbench";
import { updateMapSettings } from "src/js/stores/map/actions/updateMapSettings";
import { MapState, mapStore } from "src/js/stores/map/store";
import { updateExpandedLHS } from "src/js/stores/map/actions/updateExpandedLHS";
import { updateSelection } from "src/js/stores/map/actions/updateSelection";
import { router, useNavigate } from "src/js/router/react-router";
import Feature from "src/js/stores/user/Feature";
import ROUTES from "src/js/router/routes";

import { useBetaFeatureFlag } from "react-migration/lib/user/useFeatureFlag";
import { ProjectDropdown } from "react-migration/domains/projects/components/ProjectDropdown";
import { DashboardAnchor } from "react-migration/layouts/nav/components/DashboardAnchor/DashboardAnchor";
import { NavbarMessages } from "react-migration/layouts/nav/components/Messages/NavbarMessages";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { UnitTypeToggle } from "react-migration/components/UnitTypeToggle";
import { UpgradeButton } from "react-migration/components/UpgradeButton";
import { UserMenu } from "react-migration/domains/nav/components/UserMenu";
import { HelpMenu } from "react-migration/domains/nav/components/Actions/HelpMenu";
import { Navbar } from "react-migration/domains/nav/components/Navbar";
import { Button } from "react-migration/components/Button";
import { Avatar, AvatarSize } from "react-migration/components/Avatar";
import { isVue } from "react-migration/lib/util/isVue";
import { userStore } from "src/js/stores/user/store";

export const NavbarDesktop = ({
  children,
  activeRoute = "",
}: ComponentProps<"div" & { activeRoute: string | null }>) => {
  const { t } = useTranslation();
  const { settings } = useSnapshot(mapStore);
  const { user } = useSnapshot(userStore);
  const distractionFree = settings.state === MapState.DISTRACTION_FREE;
  const hasProjectsBeta = useBetaFeatureFlag(Feature.projectsNavBeta);
  const navigate = useNavigate();

  const mapActive = useMemo(() => {
    if (!activeRoute) return false;
    return activeRoute.includes("map") || activeRoute.includes("search") || activeRoute.includes("legacy-search");
  }, [activeRoute]);

  return (
    <Navbar.Wrapper data-testid="navbar">
      <NavbarMessages />
      <Navbar.Left>
        <div className={clsx({ "bg-white": hasProjectsBeta })}>
          <Navbar.Logo
            onClick={() => {
              if (!mapActive) isVue() ? router.push({ name: ROUTES.MAP }) : navigate(ROUTES.MAP);
              updateSelection(undefined);
              updateExpandedLHS(false);
              updateShowWorkbench(false);
            }}
          />
        </div>
        {!distractionFree && (
          <>
            {hasProjectsBeta && (
              <>
                <div className="relative bg-white">
                  <div className="absolute bottom-0 left-0 right-0 border-b border-b-border-action" />
                  <Navbar.Item
                    data-testid="navbar-projects"
                    active={window.location.pathname.includes("/workspaces")}
                    onClick={() => (isVue() ? router.push({ name: ROUTES.WORKSPACES }) : navigate(ROUTES.WORKSPACES))}
                    leadIcon="lt-home-line"
                    leadIconProps={{
                      alwaysShow: true,
                      noPaddings: true,
                    }}
                  />
                </div>
                <ProjectDropdown />
              </>
            )}
            {!hasProjectsBeta && <DashboardAnchor />}
            <Navbar.Item
              data-testid="navbar-map"
              active={!/\/(dashboard|sites|workspaces)/.test(window.location.pathname) || !window.location.pathname}
              onClick={() => (isVue() ? router.push({ name: ROUTES.MAP }) : navigate(ROUTES.MAP))}
              leadIcon="lt-map-2-line"
            >
              {t("nav.nav.map")}
            </Navbar.Item>
            <Navbar.Item
              data-testid="navbar-sites"
              active={window.location.pathname.includes("/sites")}
              onClick={() => (isVue() ? router.push({ name: ROUTES.SITES }) : navigate(ROUTES.SITES))}
              leadIcon="lt-community-line"
            >
              {t("nav.nav.sites")}
            </Navbar.Item>
          </>
        )}
      </Navbar.Left>
      {distractionFree && <Navbar.Center>{t("nav.nav.distraction_free_mode")}</Navbar.Center>}
      {!distractionFree && <Navbar.Center>{children}</Navbar.Center>}
      <Navbar.Right>
        {!distractionFree && (
          <>
            <UnitTypeToggle version="new" />
            <div className="hidden lg:inline-flex">
              <UpgradeButton source="Navbar" />
            </div>
            <HelpMenu />
            <UserMenu
              triggerElement={
                <Avatar
                  firstName={user?.firstname ?? ""}
                  lastName={user?.surname ?? ""}
                  size={AvatarSize.MD}
                  className="cursor-pointer"
                />
              }
            />
          </>
        )}
        {distractionFree && (
          <Button onClick={() => updateMapSettings("state", MapState.NORMAL)}>
            {t("nav.nav.exit_distraction_mode")}
          </Button>
        )}
      </Navbar.Right>
    </Navbar.Wrapper>
  );
};
